import { isUsd, type Currency } from '../api/types'
import type { CSSProperties, ReactNode } from 'react'
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
/* DevModeContext, not MeasurePanel. The panel imports EmptyState and Loading
   FROM this file, so importing it here would close a cycle that resolves to
   `undefined` at use rather than failing at build. The context module imports
   nothing but React and a type, which is what makes it safe to depend on from
   anywhere. */
import { Explain, ExplainMark, useExplain } from './DevModeContext'
import { noteCurrency } from './currency'

/**
 * The shared vocabulary. Everything on screen is built from these, so that a
 * new page cannot invent a fifth shade of grey or a third button height.
 *
 * Rules that hold throughout:
 *   * one-pixel rules, never boxes — a table with twelve columns cannot afford
 *     borders on everything;
 *   * navy means interactive and nothing else;
 *   * 4px spacing rhythm;
 *   * radius 6px on containers, 4px on controls. Anything rounder reads as a
 *     consumer app, which is the wrong promise for a system holding this data.
 */

/**
 * WHICH EDGE SHOULD A DROPDOWN HANG FROM?
 *
 * Every panel here is `absolute left-0` with a minimum width wider than the
 * control that opens it — 230px under a 150px picker, 250px under another.
 * That is right in the middle of a row and wrong at the end of one: measured
 * 8 Sep 2026, the last picker in the Material Planning filter bar put its
 * panel's right edge past the card and, on the narrower screens, past the
 * window — so its options were cut off with no way to reach them.
 *
 * Hanging it from the right edge instead costs nothing and is what every
 * desktop menu does. The decision is taken when the panel OPENS, from where
 * the control actually is: that is the first moment the answer is knowable,
 * and it cannot change while the panel is up.
 */
export function useDropdownEdge(
  open: boolean,
  minWidth: number,
  /* The control's own wrapper, passed in rather than created here. A new
     element would need a box to measure, and a `display: contents` one has
     none — getBoundingClientRect would return zeros and every panel would
     conclude it fits. */
  ref: { current: HTMLElement | null },
) {
  const [fromRight, setFromRight] = useState(false)
  const [style, setStyle] = useState<CSSProperties | undefined>(undefined)
  useEffect(() => {
    if (!open) return
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const vw = window.innerWidth
    // 8px of air, so a panel never sits flush against the glass.
    const right = r.left + Math.max(minWidth, r.width) > vw - 8
    setFromRight(right)

    /* NEITHER EDGE FITS ON A PHONE. Added 29 Sep 2026: a 428px company menu
       under a trigger at x=12 on a 390px screen hung from its right edge and
       started 240px off the left of the glass. When that happens — and only
       then — the panel is capped at the screen's width and slid back inside
       it. On a laptop one of the two edges always fits, so `style` stays
       undefined and the panel renders exactly as it did before. */
    const want = Math.max(minWidth, r.width)
    const width = Math.min(want, vw - 16)
    /* Measured where the trigger WILL be, not where it is this instant. The
       panel's first render hangs from the left at full width before this has
       run, spills past the glass, and focusing its search box scrolls the
       page sideways under it — by 21px, measured on a 390px phone — which
       unwinds again once the panel fits. Adding the ancestors' sideways
       scroll back takes that transient out of the arithmetic. */
    let sx = 0
    for (let p = el.parentElement; p; p = p.parentElement) sx += p.scrollLeft
    const left = right ? r.right + sx - width : r.left + sx
    let dx = 0
    if (left < 8) dx = 8 - left
    else if (left + width > vw - 8) dx = vw - 8 - (left + width)
    setStyle(
      dx !== 0 || width < want
        ? { minWidth: width, maxWidth: vw - 16, transform: dx ? `translateX(${Math.round(dx)}px)` : undefined }
        : undefined,
    )
  }, [open, minWidth, ref])
  return { fromRight, style }
}

/**
 * WHERE AN OPEN DROPDOWN PANEL GOES -- for the two pickers, SearchSelect and
 * MultiSearchSelect (1 Oct 2026). Exported 6 Oct 2026 for a third: the
 * Statement of Account's customer search, which asks the SERVER for each
 * search rather than filtering a list it already holds, and so cannot be a
 * SearchSelect -- but must open, flip and follow exactly as one does.
 *
 * THEY USED TO BE CUT OFF. The panel was `absolute` inside its control, so it
 * was clipped by the first ancestor with `overflow: hidden` -- and every Card
 * has one, for its rounded corners. The sales manager hit it on the Explorer's
 * drill-order pickers: on a short grid the card ended a row below the line and
 * the list was sliced off at the card's edge, with nothing to scroll to.
 * useDropdownEdge above fixed the sideways version of this and could never fix
 * this one, because no amount of measuring makes a clipped element visible.
 *
 * So the open panel is PORTALLED to document.body and placed with fixed
 * coordinates from the trigger's own rectangle -- nothing on the page can clip
 * it, and a transformed ancestor (the full-screen card animates in) cannot
 * re-anchor it either. And it is placed rather than merely moved:
 *   - it opens UPWARD when the room below the trigger is short of the panel's
 *     natural height and there is more room above;
 *   - the list is capped to the room it was given (never below 96px, never
 *     above the 256px it always had), so a panel is never taller than the
 *     space it opens into;
 *   - sideways it keeps useDropdownEdge's rules: hang from the left edge,
 *     from the right if that would pass the glass, and on a phone narrow to
 *     the screen and slide inside it;
 *   - it follows the trigger on scroll and resize instead of hanging in
 *     mid-air when the grid underneath moves.
 *
 * z-[60], not the z-40 it had: it now lives at the top of the document, so it
 * must clear the full-screen card and slide-overs (z-40) and the Login As
 * dialog (z-50) when it is opened from inside one -- the same layer the
 * currency hover card already uses.
 */
const PANEL_GAP = 4
const PANEL_MARGIN = 8
const PANEL_LIST_MAX = 256
const PANEL_LIST_MIN = 96
/* The panel's height apart from its list, measured in Edge 1 Oct 2026: 54px
   for the single picker (its search row); 84px for the multi-select (search
   and select-all rows), about 25px more while a search shows its "n of m"
   footer. Rounded UP -- they only decide when to open upward and how far the
   list may grow, so being a little high costs a few pixels of list, and being
   low would push the footer off the glass. */
const SINGLE_PANEL_HEAD = 60
const MULTI_PANEL_HEAD = 112

export interface PanelPlace {
  left: number
  width: number
  top?: number
  bottom?: number
  listMax: number
}

export function useFloatingPanel(
  open: boolean,
  anchor: { current: HTMLElement | null },
  minWidth: number,
  /** The panel's height apart from its list: the search row, and anything else fixed. */
  head: number,
): PanelPlace | null {
  const [place, setPlace] = useState<PanelPlace | null>(null)
  useLayoutEffect(() => {
    if (!open) {
      setPlace(null)
      return
    }
    const measure = () => {
      const el = anchor.current
      if (!el) return
      const r = el.getBoundingClientRect()
      // clientWidth, not innerWidth: a page scrollbar is not room for a panel.
      const vw = document.documentElement.clientWidth
      const vh = window.innerHeight
      const width = Math.min(Math.max(minWidth, r.width), vw - 2 * PANEL_MARGIN)
      const fromLeft = r.left + width <= vw - PANEL_MARGIN
      const left = Math.max(PANEL_MARGIN, Math.min(fromLeft ? r.left : r.right - width, vw - PANEL_MARGIN - width))
      const below = vh - r.bottom - PANEL_GAP - PANEL_MARGIN
      const above = r.top - PANEL_GAP - PANEL_MARGIN
      const up = below < head + PANEL_LIST_MAX && above > below
      const room = up ? above : below
      const next: PanelPlace = {
        left: Math.round(left),
        width: Math.round(width),
        ...(up ? { bottom: Math.round(vh - r.top + PANEL_GAP) } : { top: Math.round(r.bottom + PANEL_GAP) }),
        listMax: Math.round(Math.max(PANEL_LIST_MIN, Math.min(PANEL_LIST_MAX, room - head))),
      }
      /* Scrolling the panel's OWN list fires the capture listener too; an
         unchanged answer must not re-render the panel on every wheel tick. */
      setPlace((p) =>
        p && p.left === next.left && p.width === next.width && p.top === next.top &&
        p.bottom === next.bottom && p.listMax === next.listMax ? p : next,
      )
    }
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [open, anchor, minWidth, head])
  return place
}

/** The positioned shell both pickers render their panel into. */
export function FloatingPanel({
  place,
  panelRef,
  onKeyDown,
  children,
}: {
  place: PanelPlace
  panelRef: { current: HTMLDivElement | null }
  onKeyDown: (e: React.KeyboardEvent) => void
  children: ReactNode
}) {
  return createPortal(
    <div
      ref={panelRef}
      style={{ position: 'fixed', left: place.left, width: place.width, top: place.top, bottom: place.bottom }}
      className="z-[60] overflow-hidden rounded-md border border-(--color-line-strong) bg-(--color-surface) shadow-lg"
      onKeyDown={onKeyDown}
    >
      {children}
    </div>,
    document.body,
  )
}

export function Card({
  children,
  className = '',
  flush = false,
}: {
  children: ReactNode
  className?: string
  /** Removes the shadow — for cards that sit inside another surface. */
  flush?: boolean
}) {
  return (
    <div
      className={`overflow-hidden rounded-md border border-(--color-line) bg-(--color-surface) ${
        flush ? '' : 'shadow-(--shadow-card)'
      } ${className}`}
    >
      {children}
    </div>
  )
}

export function CardHeader({
  title,
  subtitle,
  right,
  eyebrow,
  titleRepeatsTab = false,
  rightHidesOnShort = false,
}: {
  title: string
  /* A node, not only a string, so a subtitle can carry a marked currency code
     ("Figures in SAR") for the currency hover card. Rendered as-is below. */
  subtitle?: ReactNode
  right?: ReactNode
  /** Small uppercase label above the title. Use for the data domain, not decoration. */
  eyebrow?: string
  /**
   * TRUE where the tab strip above this card already names the surface — a
   * "Purchase orders" heading directly under a selected "Purchase Orders" tab.
   * On a screen under 900px tall the title and subtitle are then dropped,
   * because they are 87px of restatement above a grid measured 161px.
   *
   * The `right` slot is NEVER dropped: it carries the export buttons, the
   * status picker, the button that clears a selection. A control that is the
   * only way out of a state must not vanish with the decoration around it.
   *
   * Do not set this where the title is the only label the content has — the
   * chart cards on Overview are titled "Cost by business unit" and nothing
   * else on screen says so.
   */
  titleRepeatsTab?: boolean
  /**
   * TRUE where the `right` slot is itself hidden under 900px because the page
   * renders that control somewhere better on a short screen. Then there is
   * nothing left in this row at all and the whole band goes, rather than
   * leaving an empty strip where the buttons used to be.
   *
   * The page MUST actually render the control elsewhere. This flag says "it
   * moved", not "it does not matter" - see the shipment views, which put
   * EXTRACT at the empty end of the filter bar under `.short-only`.
   */
  rightHidesOnShort?: boolean
}) {
  // With nothing but restatement in it, the whole row goes; with a control in
  // it, only the words do and the control keeps its place.
  const textOnly = titleRepeatsTab && (!right || rightHidesOnShort)
  return (
    // shrink-0: inside a filling Card this is chrome and must keep its height
    // while the grid below it scrolls. Without it flexbox squeezes the header
    // to make room, and the subtitle is the first thing to go.
    /* ON A PHONE THE ROW WRAPS. Beside a `shrink-0` button row, the words got
       what was left — measured at 390px, one word per line down a column 70px
       wide. `@max-3xl:` only fires when <main> is under 768px, so no laptop
       sees this; there the title and its buttons share a row as before. The
       buttons' own row may wrap too, because four of them do not fit 366px. */
    <div
      className={`flex shrink-0 items-start justify-between gap-6 border-b border-(--color-line) px-5 py-3.5 @max-3xl:flex-wrap @max-3xl:gap-3 @max-3xl:px-4 ${
        textOnly ? 'short-hide' : ''
      }`}
    >
      <div className={`min-w-0 ${titleRepeatsTab && right ? 'short-hide' : ''}`}>
        {eyebrow && <div className="eyebrow mb-1">{eyebrow}</div>}
        <h2 className="text-(length:--text-lg) font-semibold text-(--color-ink)">{title}</h2>
        {subtitle && <p className="mt-1 text-(length:--text-small) text-(--color-ink-3)">{subtitle}</p>}
      </div>
      {/* Every flex row inside the slot may wrap, not only the first: a page
          often nests its own row (status pills beside the extract buttons)
          inside the one ExpandableCard adds, and that inner row is the one
          that overflowed. */}
      {right && <div className="shrink-0 @max-3xl:max-w-full @max-3xl:[&_.flex]:flex-wrap">{right}</div>}
    </div>
  )
}

export function Button({
  children,
  onClick,
  variant = 'default',
  size = 'md',
  disabled,
  type = 'button',
  full = false,
}: {
  children: ReactNode
  onClick?: () => void
  variant?: 'default' | 'primary' | 'danger' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
  type?: 'button' | 'submit'
  full?: boolean
}) {
  const styles = {
    // The lift is 1px and the press puts it back. Small enough that nobody
    // notices it happening and everybody notices its absence — which is the
    // whole job of a hover state on the one control a screen is built around.
    primary:
      'border-transparent bg-(--color-navy-700) text-white shadow-(--shadow-card) ' +
      'hover:-translate-y-px hover:bg-(--color-navy-800) hover:shadow-(--shadow-raised) ' +
      'active:translate-y-0 active:bg-(--color-navy-900) active:shadow-(--shadow-card)',
    default:
      'border-(--color-line-strong) bg-(--color-surface) text-(--color-ink-2) hover:bg-(--color-surface-3) hover:text-(--color-ink)',
    danger:
      'border-(--color-overdue)/35 bg-(--color-surface) text-(--color-overdue) hover:bg-(--color-overdue)/8',
    ghost:
      'border-transparent bg-transparent text-(--color-ink-3) hover:bg-(--color-surface-3) hover:text-(--color-ink)',
  }[variant]

  const dims = {
    sm: 'h-7 px-2.5 text-(length:--text-small)',
    md: 'h-8 px-3.5 text-(length:--text-base)',
    lg: 'h-10 px-5 text-(length:--text-base)',
  }[size]

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-2 rounded border font-medium transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:translate-y-0 ${styles} ${dims} ${
        full ? 'w-full' : ''
      }`}
    >
      {children}
    </button>
  )
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-(length:--text-small) font-medium text-(--color-ink-2)">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1.5 block text-(length:--text-small) text-(--color-ink-3)">{hint}</span>}
    </label>
  )
}

export const inputClass =
  'w-full rounded border border-(--color-line-strong) bg-(--color-surface) px-3 py-1.5 ' +
  'text-(length:--text-base) text-(--color-ink) placeholder:text-(--color-ink-4) ' +
  'outline-none transition-colors focus:border-(--color-navy-700) ' +
  'focus:ring-2 focus:ring-(--color-navy-700)/15'

/**
 * A small status chip. Deliberately limited to four tones, each meaning one
 * thing — a fifth would start being decoration.
 */
export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode
  tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'privileged'
}) {
  const styles = {
    neutral: 'border-(--color-line-strong) bg-(--color-surface-3) text-(--color-ink-2)',
    good: 'border-(--color-good)/25 bg-(--color-good)/8 text-(--color-good)',
    warn: 'border-(--color-warn)/25 bg-(--color-warn)/8 text-(--color-warn)',
    bad: 'border-(--color-overdue)/25 bg-(--color-overdue)/8 text-(--color-overdue)',
    privileged: 'border-(--color-privileged)/30 bg-(--color-privileged)/8 text-(--color-privileged)',
  }[tone]

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-(length:--text-micro) font-semibold ${styles}`}
    >
      {children}
    </span>
  )
}

/**
 * A person, drawn the same way everywhere.
 *
 * Three grids on the Access screen list people, and before this each drew them
 * differently — one had Name and Email as separate columns, one showed the
 * address alone, one showed a name that silently fell back to an em-dash. The
 * reader had to re-learn "which of these is the person" in every table.
 *
 * Name over address in one cell, so a row is scanned once. The initial block is
 * not an avatar and is not decoration: it gives the eye a fixed left edge to
 * track down a long list, which is the thing that makes a column of addresses
 * readable at all.
 *
 * The address is ALWAYS shown, never replaced by the display name. It is the
 * key the permission table is joined on, and two people can share a name.
 */
export function PersonCell({
  name,
  email,
  note,
}: {
  name?: string | null
  email: string
  /** A short qualifier after the name — "you", "last admin". */
  note?: ReactNode
}) {
  // Initials from the display name where there is one, otherwise the address.
  // Never more than two: three-letter blocks stop being a shape and start
  // being text, and the eye is meant to track the shape.
  const source = (name || email).trim()
  const initials = source
    .split(/[\s.@_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')

  return (
    <div className="flex items-center gap-2.5">
      <span
        aria-hidden
        className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-(--color-navy-100) text-(length:--text-micro) font-semibold text-(--color-navy-700)"
      >
        {initials}
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1.5">
          <span className="truncate font-medium text-(--color-ink)">{name || email}</span>
          {note}
        </span>
        {/* Suppressed when it would simply repeat the line above it. */}
        {name && (
          <span className="block truncate text-(length:--text-small) text-(--color-ink-3)">{email}</span>
        )}
      </span>
    </div>
  )
}

/** Search, with the magnifier inside the field so it needs no label beside it. */
export function SearchInput({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string
  onChange: (v: string) => void
  placeholder: string
  /** For screen readers. The magnifier is not a label. */
  label: string
}) {
  return (
    <div className="relative">
      <svg
        aria-hidden
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-(--color-ink-4)"
      >
        <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
        <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <input
        type="search"
        aria-label={label}
        className={`${inputClass} pl-8`}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

/**
 * An inline notice: the outcome of an action, or a standing fact about the
 * screen. Sits in the flow rather than floating, because everything it carries
 * needs to be re-readable — a grant result naming four unrecognised addresses
 * is useless if it fades after three seconds.
 */
export function Notice({
  tone = 'info',
  title,
  children,
  onDismiss,
}: {
  tone?: 'info' | 'good' | 'warn' | 'bad'
  title?: string
  children: ReactNode
  onDismiss?: () => void
}) {
  const styles = {
    info: 'border-(--color-line-strong) bg-(--color-surface-3) text-(--color-ink-2)',
    good: 'border-(--color-good)/25 bg-(--color-good)/6 text-(--color-ink-2)',
    warn: 'border-(--color-warn)/30 bg-(--color-warn)/6 text-(--color-ink-2)',
    bad: 'border-(--color-overdue)/30 bg-(--color-overdue)/6 text-(--color-ink-2)',
  }[tone]

  const rule = { info: 'bg-(--color-ink-4)', good: 'bg-(--color-good)', warn: 'bg-(--color-warn)', bad: 'bg-(--color-overdue)' }[tone]

  return (
    <div
      role={tone === 'bad' ? 'alert' : 'status'}
      className={`relative flex items-start gap-3 overflow-hidden rounded-md border py-2.5 pl-4 pr-3 text-(length:--text-small) leading-relaxed ${styles}`}
    >
      <span aria-hidden className={`absolute inset-y-0 left-0 w-[3px] ${rule}`} />
      <div className="min-w-0 flex-1">
        {title && <div className="font-semibold text-(--color-ink)">{title}</div>}
        <div className={title ? 'mt-0.5' : ''}>{children}</div>
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-mr-1 shrink-0 rounded p-1 text-(--color-ink-3) transition-colors hover:bg-(--color-ink)/5 hover:text-(--color-ink)"
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  )
}

/**
 * A small counted fact in a header strip: "12 people", "2 with full data access".
 *
 * Not a StatTile. Those are 26px figures for money on a report; these answer
 * "what is the state of this list" in the space above the list, and a row of
 * big tiles for the number 2 would be a headline where a caption belongs.
 */
export function CountStat({
  label,
  value,
  tone = 'neutral',
  title,
}: {
  label: string
  value: number | string
  tone?: 'neutral' | 'warn' | 'privileged'
  title?: string
}) {
  const figure = {
    neutral: 'text-(--color-ink)',
    warn: 'text-(--color-warn)',
    privileged: 'text-(--color-privileged)',
  }[tone]

  return (
    /* A SMALL CARD, CENTRED.
       ------------------------------------------------------------------
       These were bare cells separated by a hairline rule, left-aligned. Two
       problems: a one-digit figure over a four-word label looked adrift at the
       left edge of a wide cell, and the divider made two stats read as one
       continuous strip rather than as two distinct facts.

       Centred, the figure sits over the middle of its own label whatever the
       label's length — which is the thing that makes a row of them scannable.
       The card gives each one an edge of its own, so no divider is needed.

       min-w keeps a lone "1" from collapsing to a sliver; max-w stops a long
       label stretching one card far wider than its neighbours, and the label
       wraps rather than truncating — these are captions that have to be read,
       not table cells competing for width. items-stretch on the row (at each
       call site) then squares the heights up. */
    <div
      title={title}
      className="flex min-w-[112px] max-w-[168px] flex-col items-center justify-center rounded-md border border-(--color-line) bg-(--color-surface-2) px-3 py-2.5 text-center"
    >
      <div className={`num text-[19px] font-semibold leading-none ${figure}`}>{value}</div>
      <div className="mt-1.5 text-(length:--text-small) leading-tight text-balance text-(--color-ink-3)">
        {label}
      </div>
    </div>
  )
}

/**
 * WHICH REPORT AM I ADMINISTERING?
 *
 * This platform is a registry of reports, not one report with a settings page.
 * The administration screens defaulted every call to "receivables", which was
 * invisible while one report existed and became wrong the moment a second was
 * registered — an administrator could edit a report without ever having said
 * which one.
 *
 * Deliberately NOT a tab or a segmented control: the list is open-ended. A
 * select stays legible at two reports and at twenty, and it reads as "the thing
 * I am working on" rather than "another view of the same thing".
 *
 * The scope label beside it is not decoration. A role-scoped report has no
 * column mask and no meaningful access list, so an administrator who picks one
 * and finds an empty screen needs to know that is correct rather than broken.
 */
export function ReportPicker<T extends { slug: string; displayName: string; scopingModel?: string | null }>({
  reports,
  value,
  onChange,
  busy = false,
}: {
  reports: T[] | null
  value: string
  onChange: (slug: string) => void
  busy?: boolean
}) {
  const chosen = reports?.find((r) => r.slug === value)

  return (
    <div className="flex items-center gap-2">
      <span className="eyebrow shrink-0">Report</span>
      <SearchSelect
        label="Report"
        value={value}
        onChange={onChange}
        disabled={busy || !reports || reports.length === 0}
        placeholder={reports === null ? 'Loading…' : 'Choose a report'}
        options={(reports ?? []).map((r) => ({ value: r.slug, label: r.displayName }))}
        buttonClassName="w-auto min-w-44 py-1"
      />
      {chosen?.scopingModel === 'role-scoped' && (
        <Badge tone="neutral">role-scoped</Badge>
      )}
    </div>
  )
}

/**
 * A dropdown that can be SEARCHED — the platform's replacement for every
 * native <select>.
 *
 * ---------------------------------------------------------------------------
 * WHY IT EXISTS
 *
 * The sales filter bar offers ~390 salespeople and 40 companies. A native
 * <select> answers that with a scroll wheel and first-letter guessing; finding
 * "Rana Aziz" means knowing she is under R and scrolling past everyone else.
 * The fix is a combobox: a button that opens a panel with a filter box, arrow
 * keys, and the list.
 *
 * ---------------------------------------------------------------------------
 * THE SEARCH FIELD IS ALWAYS THERE — the owner's rule, stated 6 Aug 2026:
 * every dropdown searches. Uniformity is itself a design: the panel opens the
 * same way everywhere, the cursor is always already in the filter, and the
 * flow for every list — six durations or four hundred salespeople — is the
 * same three keystrokes and Enter.
 *
 * ---------------------------------------------------------------------------
 * KEYBOARD AND SCREEN-READER CONTRACT (the ARIA combobox pattern)
 *
 * Trigger: role=combobox, aria-expanded, Enter/Space/ArrowDown open.
 * Panel: the filter input carries aria-activedescendant; ArrowUp/Down move
 * the active option (wrapping), Enter chooses it, Escape closes and returns
 * focus to the trigger, Tab closes and moves on. The list is role=listbox and
 * the chosen option carries aria-selected — the check mark is for eyes, the
 * attribute is for readers.
 */
export interface SearchSelectOption {
  value: string
  label: string
  /** Smaller second line — a code beside a name, a duration beside a label. */
  hint?: string
  /**
   * A small fixed-size thing before the label — a flag, a swatch. It owns its
   * own width and never shrinks, so rows stay aligned whether or not one is
   * present.
   */
  leading?: ReactNode
  /**
   * A short figure held against the right edge, set in tabular numerals so a
   * column of them lines up. For the value BESIDE the option rather than a
   * second description of it: a rate, a count, a size.
   */
  trailing?: string
  /** Hover text for the whole row — for the occasional row that needs a caveat. */
  title?: string
  /**
   * The key of the heading this option sits under. Only MultiSearchSelect draws headings, and
   * only when it is given `groups`; see there.
   */
  group?: string
}

/** A heading in MultiSearchSelect: the options whose `group` is its key sit under it. */
export interface SearchSelectGroup {
  key: string
  label: string
  /** Grey beside the label: a code. */
  hint?: string
  /** Held right: what the group gives now, e.g. "all teams" or "2 of 4 ticked". */
  note?: string
}

/**
 * The panel width for a menu whose members are PEOPLE, COMPANIES OR SUPPLIERS,
 * rather than codes. Pass it as `panelWidth`; the trigger stays narrow.
 *
 * MEASURED 24 Sep 2026 by RENDERING all 2,587 real labels of both reports'
 * menus in this app's own type (14px Segoe UI) and taking the widest — not by
 * counting characters, which is the wrong ruler and got this wrong once:
 * "AL REDA FOR SUPPLY OF INDUSTRIAL MATERIALS LTD." is 47 characters and
 * renders WIDER than a 49-character mixed-case name, because capitals are
 * wider. A panel sized to the longest string still cut the widest one.
 *
 * Panel = label + 84px of chrome (the checkbox, the gaps, the code column on
 * the right, and the panel's padding), all figures already including it:
 *
 *   menu                     members    p90    p99    widest
 *   company                       39    345    422    422
 *   salesperson                  438    269    327    345
 *   payment terms                 67    204    333    333
 *   supplier (sales only)      1,322    390    520    697
 *   every menu of codes           --     --     --    <= 192
 *
 * COMPANY IS NOT AN OUTLIER PROBLEM, which is what decided the number: its p90
 * is 345 against a max of 422, so the whole list is long and a panel sized to
 * its 250px trigger cut ORDINARY entries — "AL REDA FOR SUPPLY ..." beside
 * "Al-Injaz Al Dawliya for ...", two companies a reader cannot then tell apart.
 *
 * 430 covers company, salesperson and payment terms outright. SUPPLIERS ARE
 * DELIBERATELY NOT SIZED TO THEIR MAXIMUM: one Brazilian distributor needs a
 * 697px panel and the next widest needs 520, so fitting it would hand every
 * other menu a panel two thirds the width of the content pane for one row.
 * They get this width, which sits above their p90, and the long tail keeps the
 * `title` tooltip every option row already carries.
 *
 * Menus of codes — business unit, business team, region, aging period,
 * transaction type, year — top out at 192 and keep the 250 default.
 */
export const NAME_PANEL_WIDTH = 430

export function SearchSelect({
  label,
  options,
  value,
  onChange,
  placeholder = 'Choose…',
  disabled = false,
  buttonClassName = '',
  panelWidth = 250,
  keepCase = false,
}: {
  /** Announced name of the control; also seeds the filter box's placeholder. */
  label: string
  options: SearchSelectOption[]
  value: string
  onChange: (v: string) => void
  placeholder?: string
  disabled?: boolean
  /** Width/py overrides for the trigger, e.g. the picker's compact form. */
  buttonClassName?: string
  /**
   * How wide the OPEN panel is, in pixels. The trigger can be narrow while the
   * list needs room — the currency picker is a 9rem button over a panel
   * carrying a flag, a code, a name and a rate.
   *
   * A number rather than a class, because the same figure decides the flip near
   * the right edge and those two must not be able to disagree.
   */
  panelWidth?: number
  /** The search box says the label as given, as MultiSearchSelect's keepCase: "Search HR role…", not "hr role". */
  keepCase?: boolean
}) {
  const said = keepCase ? label : label.toLowerCase()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const id = useId()
  /* Placed from the trigger, in a layer of its own -- see useFloatingPanel.
     The panel mounts only once it has a place, so everything that reaches
     into it (focus, scrolling the active row into view) waits for `placed`. */
  const place = useFloatingPanel(open, rootRef, panelWidth, SINGLE_PANEL_HEAD)
  const placed = place !== null

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    // The trailing figure is searchable too: somebody looking for a currency
    // may well type the rate they are checking against.
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.hint ?? '').toLowerCase().includes(q) ||
        (o.trailing ?? '').toLowerCase().includes(q),
    )
  }, [options, query])

  const chosen = options.find((o) => o.value === value)

  /* Opening starts clean: the previous visit's filter is stale context, and
     the active row starts on the CURRENT choice so Enter-Enter is a no-op
     rather than an accidental change. */
  const openPanel = () => {
    if (disabled) return
    setQuery('')
    const i = filteredIndexOf(options, value)
    setActive(i >= 0 ? i : 0)
    setOpen(true)
  }

  const close = (returnFocus: boolean) => {
    setOpen(false)
    if (returnFocus) buttonRef.current?.focus()
  }

  const choose = (v: string) => {
    onChange(v)
    close(true)
  }

  // Focus lands in the filter when there is one, on the list otherwise.
  useEffect(() => {
    if (open && placed) inputRef.current?.focus({ preventScroll: true })
  }, [open, placed])

  // The active option stays in view while arrowing through a long list.
  useEffect(() => {
    if (!open || !placed) return
    listRef.current
      ?.querySelector(`#${CSS.escape(`${id}-opt-${active}`)}`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [open, placed, active, id])

  /* Clicking anywhere else closes without stealing the click. "Anywhere else"
     means outside the control AND outside its panel: the panel is portalled
     to the end of the document, so it is no longer inside the control's own
     element, and without the second test every click on an option would
     close the panel before the option heard it. */
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node
      if (!rootRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])

  // Typing filters, so the arrows must survive a shrinking list.
  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, filtered.length - 1)))
  }, [filtered.length])

  const onKeys = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      close(true)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => (filtered.length === 0 ? 0 : (a + 1) % filtered.length))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => (filtered.length === 0 ? 0 : (a - 1 + filtered.length) % filtered.length))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (filtered[active]) choose(filtered[active].value)
    } else if (e.key === 'Tab') {
      /* Focus goes back to the trigger FIRST and the key is left alone, so the
         browser's own Tab then moves on from the trigger -- to the next control
         on the page, as before. The panel now sits at the end of the document,
         and a Tab taken from inside it would otherwise leave the page. */
      buttonRef.current?.focus({ preventScroll: true })
      setOpen(false)
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={`${id}-list`}
        aria-label={label}
        disabled={disabled}
        onClick={() => (open ? close(false) : openPanel())}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            openPanel()
          }
        }}
        className={`${inputClass} flex items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60 ${buttonClassName}`}
      >
        {/* The leading mark rides on the TRIGGER too, so what was chosen looks
            the same closed as it did in the list. */}
        <span className="flex min-w-0 items-center gap-2">
          {chosen?.leading}
          <span className={`truncate ${chosen ? '' : 'text-(--color-ink-3)'}`} title={chosen?.label}>
            {chosen?.label ?? placeholder}
          </span>
        </span>
        {/* The chevron flips so the state is visible at a glance. */}
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          aria-hidden
          className={`shrink-0 text-(--color-ink-3) transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path d="M1.5 3.5L5 7l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && place && (
        /* ABOVE THE GRIDS, AND NO LONGER INSIDE THEM. The panel sat at z-40
           inside the control until 1 Oct 2026, where a grid's sticky header
           (z-30, measured against a 39-entry company menu, 24 Sep) could not
           paint over it -- but the card around it could still CLIP it. It is
           portalled and placed now; see useFloatingPanel. */
        <FloatingPanel place={place} panelRef={panelRef} onKeyDown={onKeys}>
          <div className="border-b border-(--color-line) p-2">
            <div className="relative">
              <svg
                width="13"
                height="13"
                viewBox="0 0 14 14"
                aria-hidden
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-(--color-ink-3)"
              >
                <circle cx="6" cy="6" r="4.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
                <path d="M9.2 9.2L12.5 12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                role="searchbox"
                aria-label={`Filter ${said}`}
                aria-activedescendant={filtered[active] ? `${id}-opt-${active}` : undefined}
                placeholder={`Search ${said}…`}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setActive(0)
                }}
                className="w-full rounded border border-transparent bg-(--color-surface-2) py-1.5 pl-8 pr-2 text-(length:--text-base) text-(--color-ink) outline-none placeholder:text-(--color-ink-3) focus:border-(--color-navy-400) focus:bg-(--color-surface)"
              />
            </div>
          </div>

          <ul
            ref={listRef}
            id={`${id}-list`}
            role="listbox"
            aria-label={label}
            tabIndex={-1}
            style={{ maxHeight: place.listMax }}
            className="scroll-slim overflow-y-auto p-1 outline-none"
          >
            {filtered.length === 0 ? (
              <li className="px-2.5 py-3 text-center text-(length:--text-small) text-(--color-ink-3)">
                Nothing matches “{query.trim()}”
              </li>
            ) : (
              filtered.map((o, i) => {
                const isChosen = o.value === value
                const isActive = i === active
                return (
                  <li
                    key={o.value === '' ? '__all' : o.value}
                    id={`${id}-opt-${i}`}
                    role="option"
                    aria-selected={isChosen}
                    title={o.title}
                    onPointerMove={() => setActive(i)}
                    onClick={() => choose(o.value)}
                    className={`flex cursor-pointer items-center gap-2 rounded px-2.5 py-1.5 text-(length:--text-base) ${
                      isActive ? 'bg-(--color-surface-2)' : ''
                    } ${isChosen ? 'font-semibold text-(--color-navy-700)' : 'text-(--color-ink)'}`}
                  >
                    {/* The check owns a fixed slot so labels align whether or
                        not a row carries it. */}
                    <span className="grid w-4 shrink-0 place-items-center" aria-hidden>
                      {isChosen && (
                        <svg width="11" height="11" viewBox="0 0 12 12">
                          <path d="M2 6.5L4.8 9.2 10 3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </span>
                    {o.leading}
                    {/* THE LABEL NO LONGER TAKES THE SLACK, and the hint no
                        longer refuses to give any.

                        It was `label: flex-1 truncate` beside `hint: shrink-0`,
                        which is fine while every hint is two words and silently
                        catastrophic when one is a sentence: the hint took the
                        whole row, the label truncated to "K." and "." with the
                        code gone entirely, and the overflow put a horizontal
                        scrollbar under the list. Seen in the currency picker,
                        23 Sep 2026, but it was waiting for any long hint.

                        Now the label keeps its natural width and the HINT takes
                        the slack and truncates, which is the right way round:
                        the label is the thing being chosen. */}
                    <span className="min-w-0 shrink truncate" title={o.label}>
                      {o.label}
                    </span>
                    {o.hint && (
                      /* WITH a trailing figure the hint is a continuation of
                         the label and sits beside it — "AED  United Arab
                         Emirates Dirham" — with the figure held right. WITHOUT
                         one it keeps the right edge, exactly where every
                         existing dropdown has always put it. So this fixes the
                         overflow without restyling thirteen other controls. */
                      <span
                        className={`min-w-0 shrink truncate text-(length:--text-small) text-(--color-ink-3) ${
                          o.trailing ? 'flex-1' : 'ml-auto'
                        }`}
                        title={o.hint}
                      >
                        {o.hint}
                      </span>
                    )}
                    {o.trailing && (
                      <span className="shrink-0 tabular-nums text-(length:--text-small) text-(--color-ink-3)">
                        {o.trailing}
                      </span>
                    )}
                  </li>
                )
              })
            )}
          </ul>

          {/* Only when the filter is actually hiding rows: "39 of 391" tells
              the reader the list is a slice without them having to wonder. */}
          {query.trim() !== '' && filtered.length > 0 && (
            <div className="border-t border-(--color-line) px-3 py-1.5 text-(length:--text-micro) text-(--color-ink-3)">
              {filtered.length} of {options.length}
            </div>
          )}
        </FloatingPanel>
      )}
    </div>
  )
}

/** Index of the chosen value in the unfiltered list, for the opening highlight. */
function filteredIndexOf(options: SearchSelectOption[], value: string): number {
  return options.findIndex((o) => o.value === value)
}

/**
 * The same combobox, choosing MANY.
 *
 * Sibling of SearchSelect rather than a `multiple` flag on it: the two differ
 * in what a click means (choose-and-close vs toggle-and-stay), what the
 * trigger says, and what Enter does. One component doing both would be a
 * thicket of conditionals in every handler.
 *
 * WHAT IT KEEPS from its sibling, because a reader must not have to learn a
 * second control: always-open search box, the same panel, arrows and
 * aria-activedescendant, Escape returning focus, the "n of m" footer.
 *
 * WHAT IT CHANGES:
 *   * An empty selection means ALL — the same meaning the single control's
 *     "All" row carried. There is no "none" state to fall into, so the grid
 *     can never go blank because somebody unticked the last box. The one
 *     exception passes emptyText: a picker of what a person has added (the
 *     HR main scope, 29 Oct 2026), where none picked is none.
 *   * The trigger states the count and names the choice while it is still
 *     nameable: "REDAChem KSA", then "2 companies", then "All companies".
 *   * A checkbox, not a check mark: the box says "several of these" before
 *     the reader clicks anything.
 *   * Selected options sort to the top ON OPEN (not while typing, which would
 *     make rows jump under the cursor), so a long list stays reviewable.
 *   * Given `groups`, the options sit under a heading each, in the order the
 *     groups are given, selected first within each: a person's teams under
 *     the units they came from, when several units are picked (owner, 30 Oct
 *     2026). The heading stays at the top of the list while its own options
 *     scroll past, so a long unit never loses its name.
 */
export function MultiSearchSelect({
  label,
  /** Plural noun for the trigger: "2 companies", "All companies". */
  noun,
  options,
  value,
  onChange,
  disabled = false,
  buttonClassName = '',
  panelWidth = 250,
  emptyText,
  groups,
  keepCase = false,
}: {
  label: string
  noun: string
  options: SearchSelectOption[]
  /** Empty = all. Never a "nothing selected" state, unless emptyText says otherwise. */
  value: string[]
  onChange: (v: string[]) => void
  disabled?: boolean
  buttonClassName?: string
  /** See the prop of the same name on SearchSelect. */
  panelWidth?: number
  /**
   * What an empty selection says where empty is not "all": the HR main scope's pickers show what
   * a person has added, and none added is none (owner, 29 Oct 2026). Default "All <noun>".
   */
  emptyText?: string
  /** Headings for the options' `group` keys, in the order they are drawn. Omitted: one flat list. */
  groups?: SearchSelectGroup[]
  /**
   * The search box says the label as given, not in lower case: a name that keeps its capitals,
   * "Search Business Team…" (owner, 30 Oct 2026: always Business Unit and Business Team).
   */
  keepCase?: boolean
}) {
  const said = keepCase ? label : label.toLowerCase()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  /* Frozen at open time: sorting selected-first on every toggle would move
     the row out from under the pointer mid-click. */
  const [order, setOrder] = useState<string[] | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const id = useId()
  /* Placed in a layer of its own -- see useFloatingPanel and SearchSelect.
     The head is taller here: the search row plus the select-all row. */
  const place = useFloatingPanel(open, rootRef, panelWidth, MULTI_PANEL_HEAD)
  const placed = place !== null

  const chosen = useMemo(() => new Set(value), [value])

  const heads = useMemo(() => new Map((groups ?? []).map((g, i) => [g.key, { ...g, at: i }])), [groups])

  const ordered = useMemo(() => {
    if (!order && heads.size === 0) return options
    const rank = new Map((order ?? []).map((v, i) => [v, i]))
    // By heading first (an option under none goes last), then selected first; the sort is stable.
    const at = (o: SearchSelectOption) => heads.get(o.group ?? '')?.at ?? heads.size
    return [...options].sort(
      (a, b) => at(a) - at(b)
        || (rank.get(a.value) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.value) ?? Number.MAX_SAFE_INTEGER),
    )
  }, [options, order, heads])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return ordered
    // A heading's name and code find its options too: typing a unit's name or code lists its teams.
    const head = (o: SearchSelectOption) => {
      const h = heads.get(o.group ?? '')
      return h ? `${h.label} ${h.hint ?? ''}` : ''
    }
    return ordered.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.hint ?? '').toLowerCase().includes(q)
        || head(o).toLowerCase().includes(q),
    )
  }, [ordered, query, heads])

  /* Runs of options under one heading, each option keeping its index in `filtered`, which is
     what the arrows and aria-activedescendant count by. Headings are not options: the arrows
     pass over them. */
  const sections = useMemo(() => {
    const out: { head: (SearchSelectGroup & { at: number }) | undefined; rows: { o: SearchSelectOption; i: number }[] }[] = []
    filtered.forEach((o, i) => {
      const head = heads.get(o.group ?? '')
      const last = out[out.length - 1]
      if (last && last.head === head) last.rows.push({ o, i })
      else out.push({ head, rows: [{ o, i }] })
    })
    return out
  }, [filtered, heads])

  const openPanel = () => {
    if (disabled) return
    setQuery('')
    setActive(0)
    // Selected first, each group keeping the list's own order.
    setOrder([
      ...options.filter((o) => chosen.has(o.value)).map((o) => o.value),
      ...options.filter((o) => !chosen.has(o.value)).map((o) => o.value),
    ])
    setOpen(true)
  }

  const close = (returnFocus: boolean) => {
    setOpen(false)
    if (returnFocus) buttonRef.current?.focus()
  }

  /* Toggle and STAY open — picking three companies should be three clicks,
     not three round trips through the trigger. */
  const toggle = (v: string) => {
    onChange(chosen.has(v) ? value.filter((x) => x !== v) : [...value, v])
  }

  useEffect(() => {
    if (open && placed) inputRef.current?.focus({ preventScroll: true })
  }, [open, placed])

  useEffect(() => {
    if (!open || !placed) return
    listRef.current
      ?.querySelector(`#${CSS.escape(`${id}-opt-${active}`)}`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [open, placed, active, id])

  // Outside the control AND its portalled panel -- see SearchSelect.
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node
      if (!rootRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])

  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, filtered.length - 1)))
  }, [filtered.length])

  const onKeys = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      close(true)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => (filtered.length === 0 ? 0 : (a + 1) % filtered.length))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => (filtered.length === 0 ? 0 : (a - 1 + filtered.length) % filtered.length))
    } else if (e.key === 'Enter' || e.key === ' ') {
      // Space toggles too — the checkbox reading of the row.
      if (e.key === ' ' && query.trim() !== '') return // still typing
      e.preventDefault()
      if (filtered[active]) toggle(filtered[active].value)
    } else if (e.key === 'Tab') {
      // Back to the trigger first, then the browser's Tab -- see SearchSelect.
      buttonRef.current?.focus({ preventScroll: true })
      setOpen(false)
    }
  }

  const summary =
    value.length === 0
      ? (emptyText ?? `All ${noun}`)
      : value.length === 1
        ? (options.find((o) => o.value === value[0])?.label ?? value[0])
        : `${value.length} ${noun}`

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={`${id}-list`}
        aria-label={label}
        disabled={disabled}
        onClick={() => (open ? close(false) : openPanel())}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            openPanel()
          }
        }}
        className={`${inputClass} flex items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60 ${buttonClassName}`}
      >
        <span className={`truncate ${value.length ? 'font-medium' : 'text-(--color-ink-3)'}`} title={summary}>
          {summary}
        </span>
        {/* No count badge beside the summary: "2 companies" already IS the
            count, and saying it twice reads as two different facts. */}
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          aria-hidden
          className={`shrink-0 text-(--color-ink-3) transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path d="M1.5 3.5L5 7l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && place && (
        /* Portalled and placed -- see useFloatingPanel. Its width is still a
           RUNTIME NUMBER (panelWidth), for the reason this panel first took
           one: min-w-[250px] cannot be widened by a caller whose members are
           long, and the option row truncates rather than wrapping -- so the
           panel width IS whether the reader can read the list. "AL REDA FOR
           SUPPLY OF INDUSTRIAL MATERIALS LTD." showed as "AL REDA FOR SUPPLY
           ..." in a panel sized to its trigger. */
        <FloatingPanel place={place} panelRef={panelRef} onKeyDown={onKeys}>
          <div className="border-b border-(--color-line) p-2">
            <div className="relative">
              <svg
                width="13"
                height="13"
                viewBox="0 0 14 14"
                aria-hidden
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-(--color-ink-3)"
              >
                <circle cx="6" cy="6" r="4.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
                <path d="M9.2 9.2L12.5 12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                role="searchbox"
                aria-label={`Filter ${said}`}
                aria-activedescendant={filtered[active] ? `${id}-opt-${active}` : undefined}
                placeholder={`Search ${said}…`}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setActive(0)
                }}
                className="w-full rounded border border-transparent bg-(--color-surface-2) py-1.5 pl-8 pr-2 text-(length:--text-base) text-(--color-ink) outline-none placeholder:text-(--color-ink-3) focus:border-(--color-navy-400) focus:bg-(--color-surface)"
              />
            </div>
          </div>

          {/* Bulk moves, stated plainly. "Select all" applies to what the
              filter currently shows — selecting 391 salespeople by accident
              is not a thing anybody wants. */}
          <div className="flex items-center justify-between gap-2 border-b border-(--color-line) bg-(--color-surface-2) px-3 py-1.5">
            <span className="text-(length:--text-micro) text-(--color-ink-3)">
              {value.length === 0 ? (emptyText ?? `All ${noun}`) : `${value.length} selected`}
            </span>
            <span className="flex gap-3">
              <button
                type="button"
                onClick={() => onChange([...new Set([...value, ...filtered.map((o) => o.value)])])}
                className="text-(length:--text-micro) font-semibold text-(--color-accent) hover:underline"
              >
                {query.trim() ? `Select these ${filtered.length}` : 'Select all'}
              </button>
              {value.length > 0 && (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-(length:--text-micro) font-semibold text-(--color-ink-3) hover:text-(--color-overdue) hover:underline"
                >
                  Clear
                </button>
              )}
            </span>
          </div>

          <ul
            ref={listRef}
            id={`${id}-list`}
            role="listbox"
            aria-label={label}
            aria-multiselectable
            tabIndex={-1}
            style={{ maxHeight: place.listMax }}
            className="scroll-slim overflow-y-auto p-1 outline-none"
          >
            {filtered.length === 0 ? (
              <li className="px-2.5 py-3 text-center text-(length:--text-small) text-(--color-ink-3)">
                Nothing matches “{query.trim()}”
              </li>
            ) : (
              sections.map(({ head, rows }) => {
                const items = rows.map(({ o, i }) => {
                  const isChosen = chosen.has(o.value)
                  const isActive = i === active
                  return (
                    <li
                      key={o.value}
                      id={`${id}-opt-${i}`}
                      role="option"
                      aria-selected={isChosen}
                      onPointerMove={() => setActive(i)}
                      onClick={() => toggle(o.value)}
                      // Under a pinned heading, an option arrowed to stops below it, not behind it.
                      className={`flex cursor-pointer items-center gap-2 rounded px-2.5 py-1.5 text-(length:--text-base) ${
                        head ? 'scroll-mt-7' : ''
                      } ${isActive ? 'bg-(--color-surface-2)' : ''} ${
                        isChosen ? 'font-semibold text-(--color-navy-700)' : 'text-(--color-ink)'
                      }`}
                    >
                      <span
                        aria-hidden
                        className={`grid h-3.5 w-3.5 shrink-0 place-items-center rounded-[3px] border ${
                          isChosen
                            ? 'border-(--color-navy-700) bg-(--color-navy-700) text-white'
                            : 'border-(--color-line-strong) bg-(--color-surface)'
                        }`}
                      >
                        {isChosen && (
                          <svg width="9" height="9" viewBox="0 0 12 12">
                            <path d="M2 6.5L4.8 9.2 10 3.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                      <span className="min-w-0 flex-1 truncate" title={o.label}>
                        {o.label}
                      </span>
                      {o.hint && (
                        <span className="shrink-0 text-(length:--text-small) text-(--color-ink-3)">{o.hint}</span>
                      )}
                    </li>
                  )
                })
                if (!head) return items
                /* A group of options, named by its heading for a screen reader. The heading is
                   pinned (sticky) inside its own group, so the next group's heading pushes it
                   away rather than stacking under it. It pins at -4px, over the list's own
                   padding, or a sliver of the row scrolled past shows above it. Its band is the
                   header shade, not the lighter one the row under the pointer takes. */
                return (
                  <li key={`group-${head.key}`} role="group" aria-labelledby={`${id}-group-${head.at}`} className="mt-1.5 first:mt-0">
                    <div
                      id={`${id}-group-${head.at}`}
                      className="sticky -top-1 z-[1] flex items-baseline gap-2 rounded bg-(--color-surface-3) px-2.5 py-1 text-(length:--text-small)"
                    >
                      <span className="min-w-0 truncate font-semibold text-(--color-ink)" title={head.label}>{head.label}</span>
                      {head.hint && <span className="shrink-0 text-(--color-ink-3)">{head.hint}</span>}
                      {head.note && (
                        <span className="ml-auto shrink-0 text-(length:--text-micro) text-(--color-ink-3)">{head.note}</span>
                      )}
                    </div>
                    <ul role="none">{items}</ul>
                  </li>
                )
              })
            )}
          </ul>

          {query.trim() !== '' && filtered.length > 0 && (
            <div className="border-t border-(--color-line) px-3 py-1.5 text-(length:--text-micro) text-(--color-ink-3)">
              {filtered.length} of {options.length}
            </div>
          )}
        </FloatingPanel>
      )}
    </div>
  )
}

/**
 * The correlation id, presented as something to copy rather than something to
 * read out. It is the only value that joins a user's screenshot to the server
 * log and the audit ledger — without it a support call is a search through
 * millions of rows.
 */
export function CorrelationId({ value }: { value: string }) {
  if (!value) return null
  return (
    <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-(--color-line) pt-3">
      <span className="eyebrow">Reference</span>
      <code className="rounded border border-(--color-line) bg-(--color-surface-3) px-2 py-1 font-mono text-(length:--text-small) text-(--color-ink-2)">
        {value}
      </code>
      <button
        type="button"
        onClick={() => void navigator.clipboard?.writeText(value)}
        className="text-(length:--text-small) font-medium text-(--color-accent) hover:underline"
      >
        Copy
      </button>
    </div>
  )
}

/**
 * Every waiting state in the platform.
 *
 * Re-exported from Brand so pages import their loader from the same place as
 * every other primitive. It was called `Loading` and rendered a rotating arc;
 * the name went with the arc, because a component called Loading that does not
 * spin is a small lie the next reader has to unpick.
 *
 * See BrandLoader for why the mark rather than a Loading.
 */
export { BrandLoader as Loading } from './Brand'

/**
 * The empty state. Not a shrug — it says what would have been here and, where
 * we know it, why it is not. An unexplained blank is the failure mode this
 * whole platform was built to remove.
 */
export function EmptyState({ title, detail }: { title: string; detail?: string }) {
  return (
    <div className="px-5 py-14 text-center">
      <p className="text-(length:--text-base) font-medium text-(--color-ink-2)">{title}</p>
      {detail && (
        <p className="mx-auto mt-1.5 max-w-md text-(length:--text-small) leading-relaxed text-(--color-ink-3)">
          {detail}
        </p>
      )}
    </div>
  )
}

/**
 * THE TWO PIECES OF CHROME EVERY REPORT OPENS WITH — its header and its rail.
 *
 * ===========================================================================
 * WHY THESE ARE COMPONENTS AND NOT A CONVENTION
 *
 * They were a convention, and the convention did not hold. Until 20 Sep 2026
 * the underlined rail existed as THREE byte-identical copies — Sales,
 * Receivables and Access — and no component anywhere. So when Material
 * Planning shipped there was nothing to import, and it reached for the nearest
 * control that looked close: `Segmented`, the pill group directly below. The
 * result was a report whose page navigation did not resemble the other two,
 * and it was nobody's decision. It was the absence of one.
 *
 * That is the failure worth fixing, rather than the one screen it produced. A
 * reader here holds several reports at once — the platform is sold as one
 * thing — and re-learning where the pages live on each of them is a cost paid
 * on every visit, by every reader, forever. A component is what makes "they
 * all look the same" survive the next report being written by somebody who
 * never read this comment. `tools/gates/assert-report-chrome.mjs` is what
 * makes it survive somebody who decides not to import it.
 *
 * ===========================================================================
 * THE RAIL IS NOT A SEGMENTED CONTROL, AND THE DIFFERENCE IS THE POINT
 *
 * `Segmented` (below) is for two or three expressions of the SAME data —
 * Local against USD, YTD against MTD. Pills, because the choice is a property
 * of what you are already looking at.
 *
 * `PageTabs` changes WHICH DATA you are looking at. It is underlined and full
 * width because it reads as pages within one document, which is what these
 * are. Using the pill group for it said "this is a view of the same figures"
 * about ten screens that share nothing but a filter bar.
 */
export function ReportHeader({
  report,
  screen,
  what,
  viewAs,
  view,
  right,
}: {
  /** The report's name — the same word the nav rail uses for it. */
  report: string
  /** Which screen of it you are on. The tab label, not the report name. */
  screen: string
  /** What this report is, in a few words: "Posted sales." */
  what?: ReactNode
  /** Whose permissions produced the figures, when that is not the reader's. */
  viewAs?: string | null
  /**
   * THE SAVED-VIEW CONTROL, on the title line rather than among the controls.
   *
   * It belongs here because it is half a label: with a view applied it shows
   * that view's NAME, and a name is a statement about what you are looking at,
   * not a word describing what a button does. Beside the heading the two read
   * as one phrase -- "Aging Dashboard, test view" -- and it costs no height,
   * which is the axis this product is short of. In the right slot it read as a
   * stray button beside the currency toggle and went unfound.
   */
  view?: ReactNode
  /** Controls belonging to the whole report — currency, period, search. */
  right?: ReactNode
}) {
  return (
    <>
      {/* no-print: on paper the eyebrow, the heading and the controls are
          furniture. What must survive the print is the attribution line
          below, which is a different sentence for a different reason. */}
      <header className="no-print flex shrink-0 flex-wrap items-start justify-between gap-4">
        <div>
          <div className="eyebrow">{report}</div>
          {/* items-baseline, so the pill sits on the heading's baseline
              rather than its box -- the two are different type sizes and
              centring them would leave the smaller one floating. It wraps
              under the title on a narrow screen, which is the right place
              for it to go. */}
          <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-(length:--text-xl) font-semibold tracking-[-0.015em] text-(--color-ink)">
              {screen}
            </h1>
            {view}
          </div>
          {/* THE SUBTITLE SAYS WHAT THE REPORT IS. It used to end in
              "Filtered to what you are permitted to see", on every screen of
              every report, and that half was removed 20 Sep 2026 at the
              owner's request: told to the person whose own permissions are
              being applied, it states the obvious, and it spent a line of the
              scarce axis doing it. There is no screen here that is NOT
              filtered — a sentence true of everything distinguishes nothing.

              THE LOGIN AS HALF STAYS, and is not the same sentence. It names
              somebody else, so it carries information the reader cannot
              otherwise have: these totals are not yours. Said on the page as
              well as in the banner because screenshots get cropped, and a
              cropped total that looks like the reader's own is exactly the
              mistake Login As has to not cause. */}
          {what || viewAs ? (
            <p className="mt-1.5 text-(length:--text-base) text-(--color-ink-3)">
              {what ? <>{what} </> : null}
              {viewAs ? (
                <>
                  Filtered to what{' '}
                  <span className="font-semibold text-(--color-privileged)">{viewAs}</span> is
                  permitted to see — not you.
                </>
              ) : null}
            </p>
          ) : null}
        </div>
        {right ? <div className="flex flex-wrap items-center gap-3">{right}</div> : null}
      </header>

      {/* THE ONE LINE THAT PRINTS. The violet banner carries an Exit button
          that means nothing on paper and is no-print, so without this a
          printed report under Login As records nothing about whose
          permissions produced the figures it shows. Material Planning was
          alone in carrying it; every report carries it now. */}
      {viewAs ? (
        <p className="hidden shrink-0 text-(length:--text-small) text-(--color-ink-3) print:block">
          Showing what <span className="font-semibold text-(--color-privileged)">{viewAs}</span> is
          permitted to see.
        </p>
      ) : null}
    </>
  )
}

/**
 * THE PAGE RAIL — which screen of this report you are on.
 *
 * See ReportHeader above for why this is a component. Two details in the
 * markup are load-bearing:
 *
 * THE BORDER IS ON THE WRAPPER, THE SCROLL IS ON THE STRIP. Ten screens share
 * one row on Material Planning and the labels are already shortened; a rail
 * that cannot scroll simply hides the last of them on a narrow content box.
 * Putting `overflow-x` on the element that also draws the 1px baseline would
 * clip the active tab's 2px underline where the two overlap — the overlap is
 * the whole visual idea — because a scroll container clips its own padding
 * box. So the wrapper draws the line and the strip inside it scrolls, with
 * the strip's own `-mb-px` lifting the tabs onto the line.
 *
 * THE WRAPPER IS MARKED no-print SEPARATELY. The print sheet already drops
 * `[role="tablist"]` by rule, but that role is on the strip, not on the
 * wrapper — unmarked, the wrapper's baseline prints as a stray rule across an
 * otherwise empty band.
 */
export function PageTabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  surface,
  measureKey,
}: {
  /** `count`: shown beside the label when above zero, for a number that asks for action. */
  tabs: { id: T; label: string; title?: string; count?: number | null }[]
  value: T
  onChange: (id: T) => void
  /** Names the group for screen readers: "Sales pages". */
  label: string
  surface?: string
  measureKey?: string
}) {
  /* THE SELECTED TAB IS KEPT IN VIEW. On a phone the rail scrolls, and the
     tab a reader is on could sit past the right edge — the page said "Week
     Comparison" while the strip showed Aging Dashboard. Only the strip's own
     scrollLeft moves, never the page, and only when the tab is actually cut
     off: where the rail fits, which is every laptop, this changes nothing. */
  const strip = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = strip.current
    const tab = el?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
    if (!el || !tab || el.scrollWidth <= el.clientWidth) return
    const left = tab.offsetLeft - el.offsetLeft
    const right = left + tab.offsetWidth
    if (left < el.scrollLeft) el.scrollLeft = left - 12
    else if (right > el.scrollLeft + el.clientWidth) el.scrollLeft = right - el.clientWidth + 12
  }, [value])
  return (
    <div className="no-print shrink-0 border-b border-(--color-line)">
      {/* MEASURED 20 Sep 2026, both supported screens: the widest rail is
          Material Planning's ten screens, and it needs 1047px. The content box
          is 1208px at 1536x864 and 1144px at 1280x720, so nothing scrolls on
          any screen the platform supports and `overflow-x` is insurance
          rather than a layout. Below about 1100px it engages, and a scrollbar
          costs 8px of the scarce axis — `thin` rather than the 15px default,
          which also pushed the active tab's underline that far off the
          baseline. Narrower than the 1280 floor is out of scope (see
          docs/RESPONSIVE.md); degrading to a scroller is simply better than
          hiding the last four screens, which is what a plain flex row does. */}
      <div
        ref={strip}
        role="tablist"
        aria-label={label}
        className="-mb-px flex gap-1 overflow-x-auto [scrollbar-width:thin]"
      >
        {tabs.map((t) => {
          const on = t.id === value
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={on}
              type="button"
              title={t.title}
              onClick={() => onChange(t.id)}
              /* NO `-mb-px` HERE — the strip carries it, once. Both of them
                 carrying it pulls twice, and the active tab's underline lands
                 a pixel BELOW the baseline instead of on it. Measured at
                 1536x864: the tab's bottom edge sat 1px under the wrapper's.

                 shrink-0 and whitespace-nowrap: inside a scroller a flex item
                 will otherwise give up width and break its label across two
                 lines, doubling the rail's height. A tab label is a name, not
                 prose — it scrolls out of view rather than wrapping. */
              className={`shrink-0 border-b-2 px-3.5 py-2 whitespace-nowrap text-(length:--text-base) transition-colors ${
                on
                  ? 'border-(--color-navy-700) font-semibold text-(--color-navy-700)'
                  : 'border-transparent font-medium text-(--color-ink-3) hover:border-(--color-line-strong) hover:text-(--color-ink)'
              }`}
            >
              {t.label}
              {t.count ? <span className="sr-only">, </span> : null}
              {t.count ? (
                <span className="ml-1.5 rounded-full bg-(--color-navy-700)/10 px-1.5 py-px text-(length:--text-micro) font-semibold tabular-nums text-(--color-navy-700)">
                  {t.count.toLocaleString('en-GB')}
                </span>
              ) : null}
            </button>
          )
        })}
        {/* THE MARK IS A SIBLING, NOT A WRAPPER. `Explain` renders a button,
            and a button inside a button is invalid HTML that browsers repair
            by hoisting the inner one out — so the documented control cannot
            wrap a tab label the way it wraps a Segmented's name. ExplainMark
            exists for exactly this, and renders nothing at all unless the
            reader has developer mode on and there is something behind it. */}
        {measureKey ? (
          <span className="flex shrink-0 items-center pl-1">
            <ExplainMark surface={surface} measureKey={measureKey} label={label} />
          </span>
        ) : null}
      </div>
    </div>
  )
}

/**
 * A segmented control — two or three mutually exclusive views of the SAME data.
 *
 * Not tabs (those change what you are looking at) and not a toggle (that turns
 * something on). This changes how one thing is expressed, which is exactly the
 * currency case.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  surface,
  measureKey,
}: {
  options: { value: T; label: string; title?: string }[]
  value: T
  onChange: (v: T) => void
  label: string
  /**
   * Developer mode: which documented control this is.
   *
   * THIS ONE NEEDED A VISIBLE LABEL TO CLICK, and did not have one — the group
   * carries its name only as an aria-label, because the options ("YTD", "MTD")
   * say what it does. So when, and ONLY when, there is something to explain and
   * the reader has asked to see it, the name appears beside the group as a
   * clickable eyebrow. Nobody else's screen changes by a pixel.
   *
   * Period is worth the trouble: it is not a filter over rows at all, it is the
   * control that decides WHICH PHYSICAL COLUMNS every figure on the report is
   * read from — the ERP's pre-computed period columns for YTD and MTD, the
   * per-row amounts for a custom range. Two figures that disagree usually
   * disagree because of this, and nothing on screen has ever said so.
   */
  surface?: string
  measureKey?: string
}) {
  const group = (
    /* On a phone a five-option group is wider than the screen, and the last
       options were simply cut off. Under a 768px content box the options wrap
       onto a second line inside the same border. Scrolling them sideways was
       tried first and does not work here: the group sits in rows whose own
       minimum width is the group's, so nothing above it can shrink. Laptops
       never reach that width. */
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex gap-0.5 rounded-md border border-(--color-line-strong) bg-(--color-surface-2) p-0.5 @max-3xl:flex-wrap"
    >
      {options.map((o) => {
        const on = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            title={o.title}
            onClick={() => onChange(o.value)}
            /* whitespace-nowrap: below 1180px "Company Overview" and
               "Inventory Position" broke across two lines inside their own
               pill, doubling the strip's height and making the row look
               damaged rather than dense. A tab label is a name, not prose. */
            className={`rounded px-3 py-1 whitespace-nowrap text-(length:--text-small) font-semibold transition-colors ${
              on
                ? 'bg-(--color-surface) text-(--color-navy-700) shadow-(--shadow-card)'
                : 'text-(--color-ink-3) hover:text-(--color-ink)'
            }`}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )

  if (!measureKey) return group

  /* The name only appears when the panel is actually reachable. `Explain`
     renders a bare span otherwise, so this branch costs a wrapper div and no
     visible change for a reader who has not asked for developer mode. */
  return (
    <SegmentedWithName surface={surface} measureKey={measureKey} label={label}>
      {group}
    </SegmentedWithName>
  )
}

/** Splits out so the hook is not called conditionally in Segmented. */
function SegmentedWithName({
  surface,
  measureKey,
  label,
  children,
}: {
  surface?: string
  measureKey: string
  label: string
  children: ReactNode
}) {
  const { can } = useExplain()
  if (!can(surface, measureKey)) return <>{children}</>
  return (
    <span className="inline-flex items-center gap-1.5">
      <Explain surface={surface} measureKey={measureKey} className="eyebrow">
        {label}
      </Explain>
      {children}
    </span>
  )
}

/**
 * Compact figure for stat tiles: 48,741,007,002 -> "48.74B".
 *
 * Anatomy taken from the deployed Fabric app's fmtCompact: two decimals at B
 * and M, one at K, and a REAL minus sign (U+2212) rather than a hyphen, which
 * sets at digit width and lines up in a column of figures.
 *
 * The full untruncated value belongs in the tooltip, never dropped — a KPI is a
 * headline, and somebody always needs the exact number under it.
 */
/**
 * Abbreviated money for the KPI tiles, with the same unit rule as money():
 * $45.34B / SAR 45.34B / 45.34B when the figure spans currencies.
 */
export function compact(n: number, currency?: Currency, code?: string | null): string {
  const a = Math.abs(n)
  const sign = n < 0 ? '\u2212' : ''
  // The unit goes AFTER the minus sign, not before it: −$1.44B, not $−1.44B.
  // Advance payments are negative on this report, so this case is on screen by
  // default rather than being an edge case somebody meets later.
  const unit = isUsd(currency) ? '$' : code ? `${code} ` : ''
  // Recorded so the currency hover card will name this code (currency.ts).
  if (isUsd(currency)) noteCurrency('USD')
  else if (code) noteCurrency(code)
  if (a >= 1e9) return `${sign}${unit}${(a / 1e9).toFixed(2)}B`
  if (a >= 1e6) return `${sign}${unit}${(a / 1e6).toFixed(2)}M`
  if (a >= 1e3) return `${sign}${unit}${(a / 1e3).toFixed(1)}K`
  return `${sign}${unit}${a.toLocaleString('en-US', { maximumFractionDigits: 2 })}`
}

/**
 * A COLLAPSIBLE, SLIM HEADER over a report's KPI tiles.
 *
 * ---------------------------------------------------------------------------
 * WHY IT EXISTS
 *
 * The report pages stack four stat tiles above a data table. On a laptop the
 * tiles are tall enough that the table below them showed barely two rows — the
 * reader came for the data and got the headline. This wraps the tiles in a thin
 * bar that HIDES them on demand, handing every pixel back to the table. The
 * tiles are also rendered `dense` by each page, so even expanded they are about
 * half their old height; collapsing is for the reader who wants the grid alone.
 *
 * ---------------------------------------------------------------------------
 * THE CHOICE IS REMEMBERED, PER REPORT
 *
 * Persisted in localStorage under `storageKey`, so a reader who hides the strip
 * on Sales finds it hidden next time — across tab switches, navigation and
 * reloads — while Receivables keeps its own answer. Default is expanded: a first
 * visit should show the figures, not an unexplained empty bar.
 *
 * Motion (the chevron spin, the hover tint) is switched off globally under
 * prefers-reduced-motion by the rule at the foot of styles.css, so nothing here
 * needs its own guard.
 */
export function CollapsibleStats({
  storageKey,
  label = 'Key figures',
  children,
}: {
  /** Distinct per report — "stats:sales", "stats:receivables" — so each page
      remembers its own choice rather than sharing one flag. */
  storageKey: string
  /** The short name for what is being hidden. */
  label?: string
  children: ReactNode
}) {
  const key = `mbi:${storageKey}`

  // Lazy initial read so a strip the reader collapsed never flashes open on
  // mount before an effect could correct it. Wrapped because localStorage
  // throws in private-mode Safari and where storage is denied — a remembered
  // preference must never be able to break the page it decorates.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      const remembered = localStorage.getItem(key)
      // A REMEMBERED CHOICE WINS FOREVER. Only when the reader has never
      // touched this strip does the screen get a say.
      if (remembered !== null) return remembered === '1'
    } catch {
      // localStorage throws in private-mode Safari and where storage is
      // denied — a remembered preference must never break the page it
      // decorates. Fall through to the height rule.
    }
    try {
      /* SHORT SCREENS OPEN COLLAPSED, ONCE.
         Measured 8 Sep 2026 at 1280x720: this strip is 137px on Sales and
         119px on Material Planning, above a grid whose scroll box was 47px —
         a third of one row. The figures are a summary of the grid underneath;
         when there is only room for one, the grid is the one worth showing.
         900px is the literal the stylesheet's short-screen step uses, so the
         strip and the cell density now agree about what "short" means. It read
         820px until 9 Sep 2026 while the stylesheet read 900 — a gap that was
         invisible because the comment claimed they matched. What it cost: a
         15.6in laptop at 125% scaling is 1536x864, which cleared 820 and so
         opened on the headline, leaving 5.8 rows of grid under it. */
      return !window.matchMedia('(min-height: 900px)').matches
    } catch {
      return false
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(key, collapsed ? '1' : '0')
    } catch {
      // A preference we cannot persist is not worth throwing over.
    }
  }, [key, collapsed])

  const panelId = useId()

  return (
    <div>
      {/* THE SLIM BAR — the only thing left when collapsed. One button, so the
          whole row is a single keyboard stop; aria-expanded carries the state
          to a screen reader, while the chevron and the Hide/Show word are the
          same state drawn twice, one for the eye and one for the reader who
          scans words rather than shapes. */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        aria-controls={panelId}
        className="flex w-full items-center gap-2 rounded-md border border-(--color-line) bg-(--color-surface-2) px-3 py-1.5 text-left transition-colors hover:bg-(--color-surface-3)"
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          aria-hidden
          className={`shrink-0 text-(--color-ink-3) transition-transform ${collapsed ? '' : 'rotate-90'}`}
        >
          <path d="M3 1l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="eyebrow">{label}</span>
        <span className="ml-auto text-(length:--text-small) font-medium text-(--color-ink-3)">
          {collapsed ? 'Show' : 'Hide'}
        </span>
      </button>

      {/* HIDDEN WITH display:none, NOT unmounted. `hidden` gives back the full
          layout height and takes the tiles out of the tab order and the
          accessibility tree — so the strip is genuinely gone for the reader who
          collapsed it — while the children stay MOUNTED. That matters: the
          Sales strip loads the freshness watermark and the figures the Explorer
          export carries as a side effect of rendering, and unmounting it would
          silently stop that fetch. Collapse is a layout choice; it must not
          change what the page knows. */}
      <div id={panelId} hidden={collapsed} className="mt-2">
        {children}
      </div>
    </div>
  )
}

/**
 * A KPI tile. Label, compact value, optional secondary line, and a 3px accent
 * band across the top where the number carries a warning.
 *
 * Structure is the Fabric app's StatTile; the colours are ours. The accent is
 * used on two tiles only — total and overdue — because a strip where every tile
 * is coloured has no emphasis left to give.
 */
export function StatTile({
  label,
  value,
  fullValue,
  secondary,
  tone = 'none',
  dense = false,
  surface,
  measureKey,
}: {
  label: string
  value: string
  /** The exact figure, for the tooltip. A headline should never be the only copy. */
  fullValue?: string
  secondary?: ReactNode
  tone?: 'none' | 'accent' | 'bad'
  /**
   * Developer mode: which documented surface and key this tile shows.
   *
   * Give both and the tile's LABEL becomes clickable for a System Admin with
   * the switch on, opening the same panel a grid header does. Omit either and
   * the tile is exactly what it was.
   *
   * THE HEADLINE FIGURES ARE THE ONES MOST WORTH EXPLAINING, and until this
   * existed they were the ones that could not be. The KPI definitions were
   * seeded into the database in the very first pass and stayed unreachable for
   * a month, because making them reachable meant threading developer-mode state
   * into another component by hand and nobody did. Two props and a context is
   * what stopped that repeating for every strip on the platform.
   */
  surface?: string
  measureKey?: string
  /** Half-height form for a report's KPI strip, where the table below it is the
      thing the reader came for and every row of stat eats a row of data. Same
      anatomy — smaller padding, a smaller figure, tighter leading — so a dense
      tile still reads as the same object, just quieter. */
  dense?: boolean
}) {
  const band = { none: '', accent: 'bg-(--color-navy-700)', bad: 'bg-(--color-overdue)' }[tone]
  const secondaryTone = tone === 'bad' ? 'text-(--color-overdue)' : 'text-(--color-ink-3)'

  return (
    <div
      /* No `flex-1`. These live in `.stat-strip`, a grid whose column widths
         are decided once for the whole strip; a grow factor there did nothing
         but let a short last row stretch its tiles across the full width. */
      className={`relative overflow-hidden rounded-md border border-(--color-line) bg-(--color-surface) shadow-(--shadow-card) ${
        dense ? 'min-w-[170px] px-3.5 py-2' : 'min-w-[190px] px-4 py-3.5'
      }`}
    >
      {band && <span aria-hidden className={`absolute inset-x-0 top-0 h-[3px] ${band}`} />}
      <div className="text-(length:--text-small) text-(--color-ink-3)">
        {measureKey ? (
          <Explain surface={surface} measureKey={measureKey}>
            {label}
          </Explain>
        ) : (
          label
        )}
      </div>
      <div
        title={fullValue}
        className={`num font-semibold leading-[1.1] text-(--color-ink) ${
          dense ? 'mt-0.5 text-[19px]' : 'mt-1.5 text-[26px]'
        }`}
      >
        {value}
      </div>
      {secondary && (
        <div className={`text-(length:--text-small) ${secondaryTone} ${dense ? 'mt-0.5' : 'mt-1.5'}`}>
          {secondary}
        </div>
      )}
    </div>
  )
}

/**
 * Money with its unit, following the rule the Fabric report uses:
 *
 *   USD mode                       ->  $1,234
 *   local, one known currency      ->  SAR 1,234
 *   local, spanning several        ->  1,234        (no symbol at all)
 *
 * THE THIRD CASE IS THE IMPORTANT ONE. A business unit covering Saudi and
 * Tunisian companies has a total that is a sum of riyals and dinars. Stamping
 * either code on it would be a lie the reader has no way to detect -- the
 * Fabric app''s own helper calls doing so "false labeling", and this follows it.
 * The database returns NULL for `code` on exactly those nodes.
 *
 * Called with no currency at all it still returns a bare number, so the many
 * existing call sites that format counts and deltas are unaffected.
 */
/**
 * The one sentence that says what unit the figures on screen are in.
 *
 * It exists because the same sentence was hand-written at roughly forty sites —
 * card subtitles, export context lines, the dev-mode request summary — each as
 * its own `currency === 'usd' ? … : …`. Every one of them would have gone on
 * saying "a mixture of functional currencies" over a screen restated in
 * dirhams, and type-checked while doing it.
 *
 * The custom arm names the RATE as well as the code, and says plainly that this
 * is a presentation conversion. A restated figure is not what the ERP booked,
 * and a reader comparing it against an invoice needs to know that before they
 * conclude the platform is wrong.
 */
/**
 * The same thing in three words, for a tile's second line or a filter summary
 * where the full sentence would not fit.
 *
 * "restated" is doing real work in the custom arm. Without it the chip reads
 * "AED" and a reader takes it for what the ERP booked; the figures are dollars
 * multiplied by a rate, and the difference matters the moment somebody
 * reconciles one against an invoice.
 */
export function currencyShort(currency: Currency): string {
  if (currency.mode === 'usd') return 'US dollars'
  if (currency.mode === 'custom') return `${currency.code}, restated`
  return 'Local, as booked'
}

export function currencyNote(currency: Currency): string {
  if (currency.mode === 'usd') return 'US dollars, converted at source'
  if (currency.mode === 'custom') {
    return `${currency.code}, restated from US dollars at ${currency.rate} per dollar — a presentation conversion, not the amount booked`
  }
  return 'Local, as booked — a mixture of functional currencies'
}

export function money(n: number, currency?: Currency, code?: string | null): string {
  const digits = n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
  // Recorded so the currency hover card will name this code (currency.ts).
  if (isUsd(currency)) {
    noteCurrency('USD')
    return `$${digits}`
  }
  if (code) noteCurrency(code)
  return code ? `${code} ${digits}` : digits
}

export function relativeTime(iso: string): string {
  if (!iso) return ''
  const ms = new Date(iso).getTime() - Date.now()
  if (Number.isNaN(ms)) return ''
  const mins = Math.round(ms / 60_000)
  if (mins <= 0) return 'now'
  if (mins < 60) return `${mins} min`
  const hours = Math.round(mins / 60)
  return hours < 24 ? `${hours} hr` : `${Math.round(hours / 24)} d`
}

/**
 * KEEP SVG TEXT THE SIZE IT SAYS IT IS.
 *
 * A chart drawn in a viewBox and laid out with `w-full` scales its whole
 * coordinate system to the container — geometry AND type. That is right for
 * the lines and wrong for the labels: measured 8 Sep 2026, the Sales trend
 * plot rendered at scale 0.71 on a 1280px laptop, so `fontSize={11}` reached
 * the reader at 7.80px and the month labels at 7.45px. On the 1920px monitor
 * it was designed on, the same labels are 13.6px. The axis of a financial
 * chart is not decoration; if it cannot be read the chart cannot be read.
 *
 * This returns a multiplier that cancels the scale: use `fontSize={11 * k}`
 * and the label arrives at 11px at every width. It is deliberately NOT applied
 * to strokes or geometry, which should keep scaling.
 *
 * Falls back to 1 wherever ResizeObserver is absent, which renders exactly
 * today's behaviour rather than nothing.
 */
export function useSvgTextScale(viewBoxWidth: number) {
  const ref = useRef<SVGSVGElement | null>(null)
  const [k, setK] = useState(1)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => {
      const w = el.getBoundingClientRect().width
      // Guard the zero-width first paint and anything absurd: a runaway
      // multiplier would put 200px type on the axis.
      if (w > 0) setK(Math.min(3, Math.max(0.5, viewBoxWidth / w)))
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [viewBoxWidth])

  return { svgRef: ref, k }
}
